import { Card, CardContent, Typography } from '@mui/material';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SellerTagType } from '@/lib/types/feature/seller-tag';

interface TagTypeListProps {
  tagTypes: SellerTagType[];
  selectedTypeId: string | null;
  onSelect: (tagTypeId: string) => void;
  onAdd: () => void;
  onRename: (tagType: SellerTagType) => void;
  onDelete: (tagType: SellerTagType) => void;
}

/**
 * タグ種別の一覧（画面右上）。
 * ここで選んだタグ種別が、下のタグ名一覧と店舗一覧の「編集中の列」になる。
 */
export function TagTypeList({
  tagTypes,
  selectedTypeId,
  onSelect,
  onAdd,
  onRename,
  onDelete,
}: TagTypeListProps) {
  return (
    <Card variant="outlined">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <Typography variant="subtitle2" fontWeight={700}>
          タグ種別
        </Typography>
        <Button type="button" variant="ghost" size="sm" onClick={onAdd}>
          <Plus className="mr-1 h-4 w-4" />
          追加
        </Button>
      </div>

      {tagTypes.length === 0 ? (
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            タグ種別がまだありません。「追加」から作成してください。
          </Typography>
        </CardContent>
      ) : (
        <div className="flex flex-col">
          {tagTypes.map((tagType) => {
            const sellerCount = tagType.tags.reduce((sum, v) => sum + v.seller_count, 0);
            const selected = tagType.id === selectedTypeId;
            return (
              <div
                key={tagType.id}
                className={`flex items-center border-b last:border-b-0 hover:bg-gray-50 ${
                  selected ? 'bg-blue-50 shadow-[inset_3px_0_0_#1976d2]' : ''
                }`}
              >
                <button
                  type="button"
                  aria-current={selected}
                  onClick={() => onSelect(tagType.id)}
                  className="flex min-w-0 flex-1 items-baseline justify-between gap-2 px-4 py-2.5 text-left"
                >
                  <span
                    className={`truncate text-sm ${selected ? 'font-medium text-blue-700' : ''}`}
                    title={tagType.name}
                  >
                    {tagType.name}
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-gray-500">
                    {tagType.tags.length}件 / {sellerCount}店
                  </span>
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={`${tagType.name} の操作`}
                      className="mr-2 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onRename(tagType)}>
                      名前を変更
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDelete(tagType)} className="text-red-600">
                      削除
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
